import {
  Classes,
  Divider,
  Drawer,
  H5,
  Position,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { LayoutAlgorithmType } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';
import {
  ProjectBaseSettingsContext,
  useProjectBaseSettingsValue,
} from 'src/containers/ProjectSettingsDrawer/hooks/useProjectBaseSettingsValue';
import {
  ProjectModelSettingsContext,
  useProjectModelSettingsValue,
} from 'src/containers/ProjectSettingsDrawer/hooks/useProjectModelSettingsValue';
import {
  ProjectSettingsValidationContext,
  useProjectSettingsValidationValue,
} from 'src/containers/ProjectSettingsDrawer/hooks/useProjectSettingsValidationValue';
import { ProjectSettingsDrawerAdvancedDetails } from 'src/containers/ProjectSettingsDrawer/ProjectSettingsDrawerAdvancedDetails';
import { ProjectSettingsDrawerFooter } from 'src/containers/ProjectSettingsDrawer/ProjectSettingsDrawerFooter';
import { ProjectSettingsDrawerPrefixTable } from 'src/containers/ProjectSettingsDrawer/ProjectSettingsDrawerPrefixTable';
import { projectSelector } from 'src/selectors/project';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const ProjectSettingsDrawerBody = () => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const projectBaseSettingsValue = useProjectBaseSettingsValue();
  const projectModelSettingsValue = useProjectModelSettingsValue();

  const {
    description,
    name,
    projectLayout,
    setDescription,
    setName,
    setProjectLayout,
  } = projectBaseSettingsValue;
  const {
    sortedModelIds,
    modelNamespaces,
    modelPrefixes,
    shouldPublishPrefixByModel,
  } = projectModelSettingsValue;
  const projectSettingsValidationValue = useProjectSettingsValidationValue({
    sortedModelIds,
    modelNamespaces,
    modelPrefixes,
    shouldPublishPrefixByModel,
  });
  const { withValidation } = projectSettingsValidationValue;

  return (
    <ProjectBaseSettingsContext.Provider value={projectBaseSettingsValue}>
      <ProjectModelSettingsContext.Provider value={projectModelSettingsValue}>
        <ProjectSettingsValidationContext.Provider
          value={projectSettingsValidationValue}
        >
          <div
            className={Classes.DRAWER_BODY}
            data-testid={TEST_IDS.projectSettingsDrawer.container}
          >
            <InlineEditableLabel
              label={copy.components.projectSettingsDrawer.name}
              monospaceText={false}
              onSetValue={setName}
              onValidate={withValidation('name')}
              placeholder={project?.name || ''}
              testId={TEST_IDS.projectSettingsDrawer.name}
              value={name}
            />
            <InlineEditableLabel
              label={copy.components.projectSettingsDrawer.description}
              minLines={3}
              monospaceText={false}
              multiline
              onSetValue={setDescription}
              placeholder={project?.description || ''}
              testId={TEST_IDS.projectSettingsDrawer.description}
              value={description}
            />
            <RadioGroup
              className="sd-drawer-project-settings__radio-group"
              inline
              label={copy.components.projectSettingsDrawer.layout.layoutLabel}
              onChange={({ currentTarget: { value } }) => {
                setProjectLayout(value as LayoutAlgorithmType);
              }}
              selectedValue={projectLayout}
            >
              {Object.values(LayoutAlgorithmType)
                .sort(
                  alphaSort(
                    (algorithm) =>
                      copy.components.projectSettingsDrawer.layout[algorithm]
                  )
                )
                .map((algorithm) => (
                  <Radio
                    key={algorithm}
                    checked={projectLayout === algorithm}
                    label={
                      copy.components.projectSettingsDrawer.layout[algorithm]
                    }
                    value={algorithm}
                  />
                ))}
            </RadioGroup>

            <Divider />
            <ProjectSettingsDrawerAdvancedDetails />
            <Divider />
            <H5>{copy.components.projectSettingsDrawer.modelSettings}</H5>
            <ProjectSettingsDrawerPrefixTable />
          </div>
          <ProjectSettingsDrawerFooter />
        </ProjectSettingsValidationContext.Provider>
      </ProjectModelSettingsContext.Provider>
    </ProjectBaseSettingsContext.Provider>
  );
};

export const ProjectSettingsDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectSettingsDrawerOpen
  );

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: false }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-project-settings"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.projectSettingsDrawer.title}
    >
      <ProjectSettingsDrawerBody />
    </Drawer>
  );
};
